import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import ContractorProfile from './components/ContractorProfile';
import ContractorsList from './components/ContractorsList';
import UserManagement from './components/UserManagement';
import OnboardingWizard from './components/OnboardingWizard';
import SavedContracts from './components/SavedContracts';
import Settings from './components/Settings';
import ContractDetailView from './components/ContractDetailView';
import DiscoverySearch from './components/DiscoverySearch';


function ProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
    const { user, isSuperAdmin, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-primary">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-secondary text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && !isSuperAdmin) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={
                <ProtectedRoute>
                    <OnboardingWizard />
                </ProtectedRoute>
            } />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Dashboard />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/saved"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <SavedContracts />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/search"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DiscoverySearch />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/contracts"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DiscoverySearch />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/contractors"
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <ContractorsList />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/contractor/:id"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ContractorProfile />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/users"
                element={
                    <ProtectedRoute adminOnly>
                        <Layout>
                            <UserManagement />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Settings />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/contract/:id"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ContractDetailView />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/war-room"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ContractDetailView />
                        </Layout>
                    </ProtectedRoute>
                }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
